// Careers — CoGrad White Design Theme
import React, { useState, useEffect, useRef } from "react";
import "./Careers.scss";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";
import AOS from "aos";
import "aos/dist/aos.css";
import { impact } from "../data/stats";
import AsOfNote from "../components/AsOfNote";
import {
  FaMapMarkerAlt, FaClock, FaArrowRight,
  FaRocket, FaUsers, FaGraduationCap, FaHeart,
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ── Data ──────────────────────────────────────────────────────

const OPEN_POSITIONS = [
  {
    id: "frontend-engineer",
    title: "Frontend Engineer",
    department: "Engineering",
    location: "Noida (On-site)",
    type: "Full-time",
    description:
      "Build delightful, accessible learning experiences in React for students and educators across India.",
    deptColor: "blue",
  },
  {
    id: "ai-ml-engineer",
    title: "AI / ML Engineer",
    department: "Engineering",
    location: "Noida / Remote",
    type: "Full-time",
    description:
      "Work on Medha AI and VidyaVoice — design and ship LLM-powered features that personalise learning at scale.",
    deptColor: "blue",
  },
  {
    id: "edtech-content-developer",
    title: "EdTech Content Developer",
    department: "Academics",
    location: "Noida (Hybrid)",
    type: "Full-time",
    description:
      "Design NCF-aligned curriculum, lesson plans, and assessments that schools love to teach with.",
    deptColor: "purple",
  },
  {
    id: "school-partnerships",
    title: "School Partnerships Associate",
    department: "Growth",
    location: "Noida (On-site)",
    type: "Full-time",
    description:
      "Own school onboarding end-to-end — from first conversation to a successful Cograd rollout.",
    deptColor: "green",
  },
  {
    id: "teacher-trainer",
    title: "Teacher Trainer (NCF)",
    department: "Academics",
    location: "Pan India (Travel)",
    type: "Full-time",
    description:
      "Lead in-person and virtual training programmes that help teachers transition to the National Curriculum Framework.",
    deptColor: "purple",
  },
  {
    id: "internship",
    title: "Internships",
    department: "Internships",
    location: "Noida / Remote",
    type: "Internship",
    description:
      "Engineering, content, design, or growth — if you're a student excited about education, we'd love to hear from you.",
    deptColor: "orange",
  },
];

const PERKS = [
  {
    num: "01",
    icon: <FaRocket />,
    title: "Mission that matters",
    text: "Every line of code, every lesson plan, every conversation reaches real students and teachers.",
  },
  {
    num: "02",
    icon: <FaUsers />,
    title: "Small team, real ownership",
    text: "Work directly with founders. Ship fast, see your impact, and grow without bureaucracy.",
  },
  {
    num: "03",
    icon: <FaGraduationCap />,
    title: "Learn relentlessly",
    text: "Books, courses, conferences — we back the things that make you sharper at your craft.",
  },
  {
    num: "04",
    icon: <FaHeart />,
    title: "Care for people",
    text: "Health coverage, flexible hours, and a culture that treats teammates like adults.",
  },
];

const PROCESS_STEPS = [
  {
    num: "01",
    title: "Apply Online",
    desc: "Fill out the form below. Takes about 5 minutes. No cover letter required — just be yourself.",
    time: "Today",
  },
  {
    num: "02",
    title: "Intro Call",
    desc: "A casual 15-minute chat with our team to understand your background and interests.",
    time: "3–5 days",
  },
  {
    num: "03",
    title: "Skills Round",
    desc: "A take-home task or live session tailored to your role. No trick questions.",
    time: "Within 1 week",
  },
  {
    num: "04",
    title: "Meet the Team",
    desc: "Talk to the founders and team members you'd be working with directly.",
    time: "Within 2 weeks",
  },
  {
    num: "05",
    title: "Offer",
    desc: "We move fast. Expect a decision within one day of your final conversation.",
    time: "~2 weeks total",
  },
];

const DEPARTMENTS = ["All", "Engineering", "Academics", "Growth", "Internships"];

// ── Page ──────────────────────────────────────────────────────
const Careers = () => {
  const formRef = useRef(null);
  const [activeDept, setActiveDept] = useState("All");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", position: "",
    experience: "", portfolio: "", resumeLink: "", message: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    AOS.init({ duration: 500, once: true, offset: 30, easing: 'ease-out' });
    const t = setTimeout(() => AOS.refreshHard(), 100);
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    window.scrollTo(0, 0);
    return () => clearTimeout(t);
  }, []);

  const filteredPositions =
    activeDept === "All"
      ? OPEN_POSITIONS
      : OPEN_POSITIONS.filter((p) => p.department === activeDept);

  const handleApplyClick = (positionTitle) => {
    setFormData((prev) => ({ ...prev, position: positionTitle }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name";
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) newErrors.phone = "Please enter your phone number";
    if (!formData.position.trim()) newErrors.position = "Please select a position";
    if (!formData.resumeLink.trim()) {
      newErrors.resumeLink = "Please paste a link to your resume";
    } else if (!/^https?:\/\/.+/i.test(formData.resumeLink.trim())) {
      newErrors.resumeLink = "Please enter a valid URL starting with http:// or https://";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveToDb = async (payload) => {
    await fetch(`${API_URL}/api/careers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        position: payload.position,
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        experience: payload.experience,
        portfolio: payload.portfolio,
        resumeLink: payload.resumeLink,
        message: payload.message,
      }),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);

    const templateParams = {
      to_name: "Cograd Careers",
      message: `
New job application received

Position:    ${formData.position}
Name:        ${formData.name}
Email:       ${formData.email}
Phone:       ${formData.phone}
Experience:  ${formData.experience || "Not specified"}
Portfolio:   ${formData.portfolio || "Not provided"}
Resume:      ${formData.resumeLink}

Cover note:
${formData.message || "(none)"}
      `,
    };

    try {
      // Save to Supabase first — always
      await saveToDb({ ...formData });

      // Try EmailJS — ignore if it fails
      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      ).catch(() => {});

      setSubmitted(true);
    } catch {
      setErrors((prev) => ({
        ...prev,
        submit: "Failed to send application. Please try again or email us at connect@cograd.in",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Careers - Cograd</title>
        <meta
          name="description"
          content="Join Cograd and help shape the future of education in India. Explore open roles in engineering, AI, content, and partnerships, and apply today."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Header />

      <div className="cr-page">

        {/* ── HERO ─────────────────────────────────────────────── */}
        <section className="cr-hero">
          <div className="cr-hero__inner">
            <div className="cr-hero__badges" data-aos="fade-down">
              <div className="cr-hero__badge">
                <span className="cr-hero__badge-dot" />
                We're hiring · Join our mission
              </div>
              <span className="cr-tag">Est. 2019 · Careers at Cograd</span>
            </div>
            <h1 className="cr-hero__h1" data-aos="fade-up" data-aos-delay="80">
              Build the future of<br />
              education in <span className="cr-hero__h1-blue">India.</span>
            </h1>
            <p className="cr-hero__sub" data-aos="fade-up" data-aos-delay="140">
              At Cograd, we're making world-class learning affordable and accessible
              for every student in India. Come build something that matters.
            </p>
            <div className="cr-hero__cta" data-aos="fade-up" data-aos-delay="200">
              <a href="#open-positions" className="cr-btn cr-btn--primary cr-btn--lg">
                See open roles
              </a>
              <button
                type="button"
                className="cr-btn cr-btn--ghost cr-btn--lg"
                onClick={() =>
                  formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                Apply now
              </button>
            </div>
            <div className="cr-hero__stats" data-aos="fade-up" data-aos-delay="260">
              <div className="cr-hero__stat">
                <span className="cr-hero__stat-n">6</span>
                <span className="cr-hero__stat-l">Open Roles</span>
              </div>
              <div className="cr-hero__stat">
                <span className="cr-hero__stat-n">4</span>
                <span className="cr-hero__stat-l">Departments</span>
              </div>
              <div className="cr-hero__stat">
                <span className="cr-hero__stat-n">{impact.schools.display}</span>
                <span className="cr-hero__stat-l">Schools Reached</span>
              </div>
              <div className="cr-hero__stat">
                <span className="cr-hero__stat-n">6+</span>
                <span className="cr-hero__stat-l">Years of Impact</span>
              </div>
            </div>
            <AsOfNote />
          </div>
        </section>


        {/* ── WHY COGRAD ───────────────────────────────────────── */}
        <section className="cr-perks">
          <div className="cr-wrap">
            <div className="cr-section-head" data-aos="fade-up">
              <span className="cr-tag">Why Cograd</span>
              <h2 className="cr-h2">Work that makes a difference</h2>
              <p className="cr-lead">
                We're a small, ambitious team building AI-powered education for rural India.
                Here's what you'll get.
              </p>
            </div>
            <div className="cr-perks__grid">
              {PERKS.map((perk, i) => (
                <div
                  className="cr-perk-card"
                  key={perk.title}
                  data-aos="fade-up"
                  data-aos-delay={i * 80}
                >
                  <span className="cr-perk-card__num">{perk.num}</span>
                  <div className="cr-perk-card__icon">{perk.icon}</div>
                  <h3 className="cr-perk-card__title">{perk.title}</h3>
                  <p className="cr-perk-card__desc">{perk.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* ── INTERVIEW PROCESS ────────────────────────────────── */}
        <section className="cr-process">
          <div className="cr-wrap">
            <div className="cr-section-head" data-aos="fade-up">
              <span className="cr-tag">Our Process</span>
              <h2 className="cr-h2">What to expect</h2>
              <p className="cr-lead">
                We respect your time. The whole process takes about two weeks from applying to offer.
              </p>
            </div>
            <div className="cr-process__steps">
              {PROCESS_STEPS.map((step, i) => (
                <div
                  className="cr-process__step"
                  key={step.num}
                  data-aos="fade-up"
                  data-aos-delay={i * 80}
                >
                  <div className="cr-process__step-top">
                    <span className="cr-process__step-num">{step.num}</span>
                    {i < PROCESS_STEPS.length - 1 && (
                      <span className="cr-process__step-line" aria-hidden="true" />
                    )}
                  </div>
                  <h3 className="cr-process__step-title">{step.title}</h3>
                  <p className="cr-process__step-desc">{step.desc}</p>
                  <span className="cr-process__step-time">{step.time}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── OPEN POSITIONS ───────────────────────────────────── */}
        <section className="cr-positions" id="open-positions">
          <div className="cr-wrap">
            <div className="cr-section-head" data-aos="fade-up">
              <span className="cr-tag">Open Positions</span>
              <h2 className="cr-h2">Find your role</h2>
              <p className="cr-lead">
                Don't see a perfect fit? Apply with "Other" — we always want to meet talented people.
              </p>
            </div>
            <div className="cr-filter" data-aos="fade-up">
              {DEPARTMENTS.map((dept) => (
                <button
                  key={dept}
                  className={`cr-filter__tab${activeDept === dept ? " cr-filter__tab--active" : ""}`}
                  onClick={() => setActiveDept(dept)}
                >
                  {dept}
                </button>
              ))}
            </div>
            <div className="cr-positions__list">
              {filteredPositions.map((role, i) => (
                <div
                  className="cr-pos-card"
                  key={role.id}
                  data-aos="fade-up"
                  data-aos-delay={i * 60}
                >
                  <div className="cr-pos-card__main">
                    <span className={`cr-pos-card__dept cr-pos-card__dept--${role.deptColor}`}>
                      {role.department}
                    </span>
                    <h3 className="cr-pos-card__title">{role.title}</h3>
                    <p className="cr-pos-card__desc">{role.description}</p>
                    <div className="cr-pos-card__meta">
                      <span><FaMapMarkerAlt /> {role.location}</span>
                      <span><FaClock /> {role.type}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cr-btn cr-btn--primary cr-pos-card__apply"
                    onClick={() => handleApplyClick(role.title)}
                  >
                    Apply <FaArrowRight />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* ── APPLY FORM ───────────────────────────────────────── */}
        <section className="cr-apply" id="apply" ref={formRef}>
          <div className="cr-apply__bg-grid" aria-hidden="true" />
          <div className="cr-wrap">
            <div className="cr-apply__layout">

              <div className="cr-apply__intro" data-aos="fade-right">
                <span className="cr-tag">Apply Now</span>
                <h2 className="cr-h2">Ready to make an impact?</h2>
                <p className="cr-apply__sub">
                  Fill out the form and we'll get back to you within a few working days.
                  Have questions? Email us at{" "}
                  <a href="mailto:connect@cograd.in">connect@cograd.in</a>.
                </p>
                <ul className="cr-apply__tips">
                  <li>Upload your resume to Google Drive or Dropbox and paste a public link</li>
                  <li>Share a portfolio, GitHub, or LinkedIn so we can get to know you better</li>
                  <li>Tell us why you want to work at Cograd — we read every cover note</li>
                </ul>
              </div>

              <div className="cr-apply__form-wrap" data-aos="fade-left">
                {submitted ? (
                  <div className="cr-form__submitted">
                    <div className="cr-form__submitted-icon">✓</div>
                    <h3>Application Received!</h3>
                    <p>Thanks for applying! We've received your application and will reach out if there's a fit.</p>
                    <button
                      type="button"
                      className="cr-btn cr-btn--ghost"
                      onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", position: "", experience: "", portfolio: "", resumeLink: "", message: "" }); }}
                    >
                      Submit another application
                    </button>
                  </div>
                ) : (
                <form className="cr-form" onSubmit={handleSubmit} noValidate>
                  <div className="cr-form__row">
                    <div className="cr-form__group">
                      <label htmlFor="name">Full name *</label>
                      <input
                        id="name" type="text" name="name"
                        value={formData.name} onChange={handleChange}
                        disabled={isLoading} placeholder="Your full name"
                      />
                      {errors.name && <span className="cr-form__error">{errors.name}</span>}
                    </div>
                    <div className="cr-form__group">
                      <label htmlFor="email">Email *</label>
                      <input
                        id="email" type="email" name="email"
                        value={formData.email} onChange={handleChange}
                        disabled={isLoading} placeholder="you@example.com"
                      />
                      {errors.email && <span className="cr-form__error">{errors.email}</span>}
                    </div>
                  </div>
                  <div className="cr-form__row">
                    <div className="cr-form__group">
                      <label htmlFor="phone">Phone *</label>
                      <input
                        id="phone" type="tel" name="phone"
                        value={formData.phone} onChange={handleChange}
                        disabled={isLoading} placeholder="+91 9XXXXXXXXX"
                      />
                      {errors.phone && <span className="cr-form__error">{errors.phone}</span>}
                    </div>
                    <div className="cr-form__group">
                      <label htmlFor="position">Position *</label>
                      <select
                        id="position" name="position"
                        value={formData.position} onChange={handleChange}
                        disabled={isLoading}
                      >
                        <option value="">Select a role</option>
                        {OPEN_POSITIONS.map((role) => (
                          <option key={role.id} value={role.title}>{role.title}</option>
                        ))}
                        <option value="Other">Other / Not listed</option>
                      </select>
                      {errors.position && <span className="cr-form__error">{errors.position}</span>}
                    </div>
                  </div>
                  <div className="cr-form__row">
                    <div className="cr-form__group">
                      <label htmlFor="experience">Years of experience</label>
                      <input
                        id="experience" type="text" name="experience"
                        value={formData.experience} onChange={handleChange}
                        disabled={isLoading} placeholder="e.g. 3 years, fresher, intern"
                      />
                    </div>
                    <div className="cr-form__group">
                      <label htmlFor="portfolio">LinkedIn / Portfolio</label>
                      <input
                        id="portfolio" type="url" name="portfolio"
                        value={formData.portfolio} onChange={handleChange}
                        disabled={isLoading} placeholder="https://linkedin.com/in/..."
                      />
                    </div>
                  </div>
                  <div className="cr-form__group">
                    <label htmlFor="resumeLink">Resume link *</label>
                    <input
                      id="resumeLink" type="url" name="resumeLink"
                      value={formData.resumeLink} onChange={handleChange}
                      disabled={isLoading}
                      placeholder="Paste a Google Drive / Dropbox link to your resume"
                    />
                    {errors.resumeLink && (
                      <span className="cr-form__error">{errors.resumeLink}</span>
                    )}
                    <span className="cr-form__hint">
                      Make sure the link is set to "Anyone with the link can view".
                    </span>
                  </div>
                  <div className="cr-form__group">
                    <label htmlFor="message">Why do you want to join Cograd?</label>
                    <textarea
                      id="message" name="message" rows="4"
                      value={formData.message} onChange={handleChange}
                      disabled={isLoading}
                      placeholder="A short note on what excites you about this role"
                    />
                  </div>
                  {errors.submit && (
                    <div className="cr-form__error cr-form__error--block">{errors.submit}</div>
                  )}
                  <button
                    type="submit"
                    className={`cr-btn cr-btn--primary cr-form__submit${isLoading ? " cr-form__submit--loading" : ""}`}
                    disabled={isLoading}
                  >
                    {isLoading ? "Submitting…" : "Submit application"}
                  </button>
                </form>
                )}
              </div>

            </div>
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
};

export default Careers;
